import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SettingsGroup, SettingsPageFrame } from '@eluve/blocks';
import {
  Button,
  Caption,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FormFooter,
  FormHeader,
  FormNewDescription,
  FormSection,
  FormTitle,
  Separator,
  Stack,
  Textarea,
  useToast,
} from '@eluve/components';
import {
  LanguageSettings,
  PromptTemplateSettings,
} from '@eluve/frontend-feature-user-settings';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';
import { useBeforeUnload } from '@eluve/utility-hooks';

const EXAMPLE_INSTRUCTIONS = [
  'Remove parts about lifestyle counseling',
  'Include patient quotes verbatim',
  'Keep the plan section bullet-pointed',
  'Always end with a follow-up timeline',
];

const noteInstructionsFragment = graphql(`
  fragment NoteInstructions on TenantUserSettings {
    __typename
    userId
    tenantId
    customNoteInstructions
  }
`);

const updateCustomInstructionsMutation = graphql(
  `
    mutation UpdateCustomInstructions(
      $tenantId: uuid!
      $userId: uuid!
      $instructions: String!
    ) {
      updateTenantUserSettingsByPk(
        pkColumns: { tenantId: $tenantId, userId: $userId }
        _set: { customNoteInstructions: $instructions }
      ) {
        ...NoteInstructions
      }
    }
  `,
  [noteInstructionsFragment],
);

export const NoteSettingsPage: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const toast = useToast();
  const analytics = useAnalytics('user_settings');

  const { customNoteInstructions } =
    useCompleteFragment({
      fragment: noteInstructionsFragment,
      key: { tenantId, userId },
      strict: false,
    }) ?? {};

  const [instructions, setInstructions] = useState<string>(
    customNoteInstructions ?? '',
  );

  const isDirty =
    (Boolean(instructions) || Boolean(customNoteInstructions)) &&
    (instructions ?? null) !== (customNoteInstructions ?? null);

  useBeforeUnload(isDirty);

  const [setCustomInstructions] = useMutation(
    updateCustomInstructionsMutation,
    {
      onError: () => toast.error('Failed to save instructions'),
    },
  );

  const saveInstructions = async () => {
    await setCustomInstructions({
      variables: { tenantId, userId, instructions },
      onCompleted: (data) => {
        if (data.updateTenantUserSettingsByPk) {
          analytics.capture('custom_instructions_update', {
            tenantId,
            userId,
          });
        }
      },
    });
  };

  return (
    <SettingsPageFrame title="Note Preferences">
      <SettingsGroup
        title="Defaults"
        description="Common preferences applied to every new note."
      >
        <Field orientation="horizontal" className="px-6 py-5">
          <FieldContent>
            <FieldLabel className="whitespace-nowrap">
              Default Template
            </FieldLabel>
            <FieldDescription>
              The note format applied to new sessions.
            </FieldDescription>
          </FieldContent>
          <div className="flex w-full max-w-60 justify-end">
            <PromptTemplateSettings className="w-full" />
          </div>
        </Field>
        <TenantOrUserFeatureFlaggedComponent flag="MULTILINGUAL_SUPPORT">
          <Separator className="my-0" />
          <LanguageSettings />
        </TenantOrUserFeatureFlaggedComponent>
      </SettingsGroup>

      <FormSection className="gap-4">
        <FormHeader>
          <FormTitle>Custom instructions</FormTitle>
          <FormNewDescription>
            Customize your notes by providing specific guidelines. Try to be as
            clear and concise as possible for best results.
          </FormNewDescription>
        </FormHeader>
        <Textarea
          placeholder="Type your instructions here"
          rows={4}
          className="min-h-25 bg-bg-primary"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <FormFooter className="gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={!isDirty}
            onClick={() => setInstructions(customNoteInstructions ?? '')}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!isDirty}
            onClick={saveInstructions}
          >
            Save
          </Button>
        </FormFooter>
        <Stack gap={2} align="start">
          <Caption
            size="3xs"
            weight="bold"
            color="supporting"
            className="block tracking-[2px]"
          >
            EXAMPLES
          </Caption>
          <Stack gap={2} align="start">
            {EXAMPLE_INSTRUCTIONS.map((example) => (
              <Button
                key={example}
                variant="outline"
                size="sm"
                onClick={() => setInstructions(example)}
              >
                {example}
              </Button>
            ))}
          </Stack>
        </Stack>
      </FormSection>
    </SettingsPageFrame>
  );
};
